/**
 * Styled-components do Revisor de Ferragens.
 * 100% tokens do design system do ERP (var(--*) / theme). Sem cores do HTML
 * original (nada de vermelho de marca / petroleo).
 */
import styled from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";

export const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 20px 80px;

  @media ${mq.tablet} {
    padding: 12px 16px 96px;
  }
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

export const SearchWrap = styled.div`
  flex: 1;
  min-width: 220px;

  @media ${mq.mobile} {
    min-width: 0;
  }
`;

/* ===== Cards de projeto (selecionaveis) ===== */
export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;

  /* Tablet: 2 colunas; celular: 1 coluna compacta. */
  @media ${mq.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  @media ${mq.mobile} {
    grid-template-columns: minmax(0, 1fr);
    gap: 6px;
  }
`;

export const ProjectCard = styled.button`
  text-align: left;
  position: relative;
  background: ${({ theme }) => theme.surface || "var(--surface)"};
  border: 1px solid ${({ $selected }) => ($selected ? "var(--accent)" : "var(--border)")};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 0 1px var(--accent)" : "none")};
  border-radius: 12px;
  padding: 16px 16px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  font: inherit;
  color: inherit;

  &:hover {
    border-color: ${({ $selected }) => ($selected ? "var(--accent)" : "var(--border-strong, var(--ink-3))")};
    transform: translateY(-1px);
  }

  @media ${mq.tablet} {
    min-width: 0;
    padding: 8px 12px;
    border-radius: 9px;

    &:hover {
      transform: none;
    }
  }
`;

export const ProjectCheck = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${({ $selected }) => ($selected ? "var(--accent)" : "var(--border-strong, var(--ink-3))")};
  background: ${({ $selected }) => ($selected ? "var(--accent)" : "transparent")};
  display: grid;
  place-items: center;
  color: var(--ink-on-accent, #fff);

  @media ${mq.tablet} {
    top: 8px;
    right: 10px;
  }
`;

export const ProjectTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 2px;
  padding-right: 28px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${mq.tablet} {
    font-size: 13.5px;
    font-weight: 700;
    margin-bottom: 0;
  }
`;

export const ProjectId = styled.div`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  color: var(--ink-3);
  margin-bottom: 10px;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    margin-bottom: 2px;
  }
`;

export const ProjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12.5px;
  color: var(--ink-2);

  /* Compacto: cliente/autor/status numa linha só, separados por ponto. */
  @media ${mq.tablet} {
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 6px;
    row-gap: 0;
    font-size: var(--text-min, 12px);
    line-height: 1.35;

    > span + span::before {
      content: "· ";
      color: var(--ink-3);
    }
  }
`;

/* ===== Barra de selecao fixa ===== */
export const SelectionBar = styled.div`
  position: fixed;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  border-radius: 999px;
  padding: 8px 8px 8px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 40;

  @media ${mq.mobile} {
    left: 12px;
    right: 12px;
    bottom: 12px;
    transform: none;
    justify-content: space-between;
    border-radius: 14px;
    padding: 6px 6px 6px 14px;
  }
`;

/* ===== Editor: card de ambiente ===== */
export const AmbienteCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
`;

export const AmbienteHeader = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  @media ${mq.mobile} {
    padding: 10px 12px;
    flex-wrap: wrap;
  }
`;

export const AmbienteTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);

  @media ${mq.tablet} {
    font-size: 15px;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const AmbienteSub = styled.div`
  font-size: 12.5px;
  color: var(--ink-2);
  margin-top: 4px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

export const ModuleHeader = styled.div`
  padding: 12px 18px 6px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  border-top: 1px solid var(--border);

  @media ${mq.mobile} {
    padding: 10px 10px 4px;
    min-width: 0;
  }
`;

export const ModuleMid = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  color: var(--accent);
  background: var(--info-bg);
  padding: 2px 6px;
  border-radius: 4px;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    flex: 0 0 auto;
  }
`;

export const ModuleName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);

  @media ${mq.tablet} {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ModuleCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: var(--ink-3);

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    flex: 0 0 auto;
    white-space: nowrap;
  }
`;

/* Linha de item: grid (sem scroll lateral).
   $cols define o template: editor (5 colunas) vs lista salva (6, com conferencia). */
export const ItemRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "44px 72px 1fr 130px 32px"};
  align-items: center;
  gap: 10px;
  padding: 8px 18px;
  border-top: 1px solid var(--border);
  background: ${({ $manual, $confered }) =>
    $confered ? "var(--success-bg)" : $manual ? "var(--info-bg)" : "transparent"};
  opacity: ${({ $hidden }) => ($hidden ? 0.5 : 1)};

  /* Celular: sem a coluna de dimensões (DimCell some) e colunas estreitas —
     conferir(36) nº(24) qt(52) descrição(1fr) menu(36) cabem em 375px. */
  @media ${mq.mobile} {
    grid-template-columns: 36px 24px 52px minmax(0, 1fr) 36px;
    gap: 6px;
    padding: 6px 10px;

    > * {
      min-width: 0;
    }
  }
`;

/* Linha auxiliar abaixo da descrição (fabricante, "conferido por"). */
export const ItemNota = styled.div`
  font-size: ${({ $tamanho }) => $tamanho || "11px"};
  color: ${({ $tom }) => $tom || "var(--ink-3)"};
  margin-top: ${({ $colado }) => ($colado ? 0 : "2px")};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ColNum = styled.div`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  color: var(--ink-3);
  text-align: center;
`;

export const DimCell = styled.div`
  @media ${mq.mobile} {
    display: none;
  }
`;

export const SmallInput = styled.input`
  width: 100%;
  font: inherit;
  font-size: 13px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  color: var(--ink);
  text-align: ${({ $center }) => ($center ? "center" : "left")};

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
  &:disabled {
    background: transparent;
    border-color: transparent;
    color: var(--ink-2);
    text-decoration: ${({ $confered }) => ($confered ? "line-through" : "none")};
  }
`;

export const ConferCheck = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1.8px solid ${({ $checked }) => ($checked ? "var(--success-text)" : "var(--border-strong, var(--ink-3))")};
  background: ${({ $checked }) => ($checked ? "var(--success-text)" : "transparent")};
  cursor: pointer;
  display: grid;
  place-items: center;
  color: #fff;
  padding: 0;

  /* Alvo de toque de 36px (44px apertaria a descrição em 375px). */
  @media ${mq.tablet} {
    width: 36px;
    height: 36px;
    border-radius: 9px;
  }
`;

export const RowMenuBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: var(--ink-2);

  &:hover {
    background: var(--surface-2, rgba(0, 0, 0, 0.05));
    color: var(--ink);
  }

  @media ${mq.tablet} {
    width: 36px;
    height: 36px;
  }
`;

export const AmbienteFooter = styled.div`
  padding: 12px 18px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid var(--border);
  font-size: 12px;
  color: var(--ink-3);
`;

export const ShowHidden = styled.button`
  width: 100%;
  border: none;
  border-top: 1px solid var(--border);
  background: var(--surface-2, rgba(0, 0, 0, 0.02));
  color: var(--accent);
  font: inherit;
  font-size: 12px;
  padding: 9px;
  cursor: pointer;
`;

export const StickyActions = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 14px 0;
  background: linear-gradient(0deg, var(--bg) 70%, transparent);
  flex-wrap: wrap;
`;

export const EmptyBox = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-2);
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-2);
  margin-bottom: 4px;
`;

export const Field = styled.div`
  margin-bottom: 14px;
`;

/* ===== Histórico: card compacto de lista salva ===== */
export const HistoricoCard = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 10px;
  padding: 14px 18px;

  @media ${mq.tablet} {
    border-radius: 9px;
    padding: 7px 10px;
    margin-bottom: 6px;
  }
`;

export const HistoricoLinha = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  > .corpo {
    min-width: 0;
  }
  > .selo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .badge {
    white-space: nowrap;
  }

  @media ${mq.tablet} {
    gap: 8px;

    > .corpo {
      flex: 1 1 auto;
    }
    .badge {
      font-size: var(--text-min, 12px);
    }
  }
`;

export const HistoricoTitulo = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${mq.tablet} {
    font-size: 13.5px;
    font-weight: 700;
  }
`;

export const HistoricoMeta = styled.div`
  font-size: 12.5px;
  color: var(--ink-2);
  margin-top: 4px;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;

  svg {
    vertical-align: -1px;
  }

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    margin-top: 1px;
    column-gap: 6px;
    row-gap: 0;

    > span + span::before {
      content: "· ";
      color: var(--ink-3);
    }
  }
`;

/* ManualForm: quantidade + dimensões lado a lado; empilha no celular. */
export const LinhaQtDim = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;

  @media ${mq.mobile} {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
`;
